import useLocalStorageContextHook from '/src/hooks/useLocalStorageContextHook';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import useGetStudentClassesQuery from '@/v2/domains/Students/queries/useGetStudentClassesQuery.hook';
import { useEffect, useContext } from 'react';
import { usePlausibleContext } from '../PlausibleAnalytics/PlausibleAnalytics';
import { useRouter } from 'next/router';

export default function useStudentContext() {
  const globalContext = useContext(GlobalContext);
  const plausibleContext = usePlausibleContext();
  const router = useRouter();

  const {
    data: studentRawClasses,
    isPending,
    refetch,
  } = useGetStudentClassesQuery();

  const { state, actions } = useLocalStorageContextHook({
    contextName: 'studentContext',
    defaultState: {
      loaded: false,
      classes: null,
      currentClass: null,
      teacherPreferences: null,
      subscription: null,
    },
    actions: {
      publicActions: {
        setCurrentClass: (classId) => {
          actions.setState({
            currentClass: classId,
          });
          router.replace('/student/dashboard');
        },
        forceClassesReload: async () => {
          await refetch();
        },
      },
    },
  });

  useEffect(() => {
    if (!globalContext.state.loaded) {
      return;
    }

    if (!globalContext.state.user?.student) {
      actions.resetState({ loaded: true });
      return;
    }

    if (!studentRawClasses) {
      actions.setState({ loaded: !isPending });
      return;
    }

    const studentClasses = toStudentClasses(studentRawClasses);
    actions.setState({
      classes: studentClasses,
      loaded: true,
      currentClass: getCurrentClass(studentClasses, state.currentClass),
    });
  }, [
    globalContext.state.loaded,
    globalContext.state.user,
    studentRawClasses,
    isPending,
  ]);

  useEffect(() => {
    const selectedClass = state.classes?.find(
      (c) => c.id == state.currentClass
    );

    if (selectedClass) {
      const internalUseClass = state.classes.find((clazz) => clazz.internalUse);

      actions.setState({
        isSharedStudent: internalUseClass?.id == state.currentClass,
        isAutoPilot: !!selectedClass.isAutoPilot,
      });
    }
  }, [state.currentClass, state.classes]);

  useEffect(() => {
    if (
      state.classes &&
      state.currentClass &&
      globalContext.state.user?.student?.subscriptions
    ) {
      const selectedClass = state.classes.find(
        (c) => c.id == state.currentClass
      );

      const subscription = globalContext.state.user.student.subscriptions.find(
        (subs) => subs.id == selectedClass?.subscriptionId
      );

      actions.setState({ subscription });
    }
  }, [
    state.currentClass,
    state.classes,
    globalContext.state.user?.student?.subscriptions,
  ]);

  useEffect(() => {
    plausibleContext.actions.setStudentContextState(state);
  }, [state.loaded, state.subscription]);

  useEffect(() => {
    if (state.subscription) {
      const teacherPreferences = state.subscription?.teacher?.data?.preferences;

      actions.setState({ teacherPreferences });
    }
  }, [state.subscription]);

  return { state, actions: actions.publicActions };
}

function toStudentClasses(studentRawClasses) {
  return studentRawClasses
    .sort((a, b) => b.createdAt < a.createdAt)
    .map((stdClass) => {
      const stdClassDates = [...stdClass.classStudents].pop();
      const teacher = stdClass.subscription.teacher;
      return {
        name: stdClass.name,
        id: stdClass.id,
        teacher: `${teacher.firstName} ${teacher.lastName}`,
        movedAt: stdClassDates.movedAt,
        createdAt: stdClassDates.createdAt,
        internalUse: stdClass.internalUse,
        subscriptionId: stdClass.subscriptionId,
        isAutoPilot: stdClass.isAutoPilot,
      };
    });
}

function getCurrentClass(studentClasses, currentClass) {
  if (
    currentClass &&
    studentClasses.some((clazz) => clazz.id == currentClass)
  ) {
    return currentClass;
  }
  return studentClasses?.length == 1 ? studentClasses[0].id : null;
}
